/**
 * Tournament Detail Page
 * Route: /prioritization/tournaments/:tournamentId
 *
 * Split-pane layout:
 *   Left  — Bracket rail: vertical list of rounds
 *   Right — Round detail pane + advancement review
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Trophy,
  ChevronRight,
  ArrowLeft,
  CheckCircle,
  Circle,
  Clock,
  AlertCircle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  Award,
  Loader2,
  Play,
  Users,
  Pencil,
} from 'lucide-react';
import { useTournamentStore, persistRoundSessionIds } from '../../../stores/prioritization/tournamentStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { calculateAdvancementResult } from '../../../stores/prioritization/tournamentStore';
import type { Tournament, TournamentRound, AdvancementResult } from '../../../types/prioritization/tournament';
import { TournamentParticipantEditor } from '../../../components/prioritization/tournaments/TournamentParticipantEditor';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import { createSessionWithFeedback } from '../../../lib/sessions/createSessionFlow';
import { useToast } from '../../../components/ui/Toast';

// ─── Tool display names ───────────────────────────────────────────────────────

const TOOL_LABELS: Record<string, string> = {
  stack_ranking: 'Stack Ranking',
  pairwise_ranking: 'Pairwise Ranking',
  dot_voting: 'Dot Voting',
  buy_a_feature: 'Buy a Feature',
  impact_effort_matrix: 'Impact-Effort Matrix',
  wsjf: 'WSJF',
  rice: 'RICE Scoring',
  moscow: 'MoSCoW',
  simple_weighted_scoring: 'Weighted Scoring',
  pareto_analysis: 'Pareto Analysis',
  cost_of_delay: 'Cost of Delay',
  max_priorities_pyramid: 'Max Priorities Pyramid',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StateBadge({ state }: { state: TournamentRound['state'] }) {
  const map: Record<TournamentRound['state'], { label: string; cls: string }> = {
    PENDING:           { label: 'Pending',      cls: 'bg-[var(--hover-tint)] text-[var(--text-ter)]' },
    OPEN:              { label: 'In Progress',  cls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
    SESSIONS_COMPLETE: { label: 'Ready',        cls: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
    ADVANCING:         { label: 'Reviewing',    cls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
    COMPLETE:          { label: 'Complete',     cls: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
    SKIPPED:           { label: 'Skipped',      cls: 'bg-[var(--hover-tint)] text-[var(--text-ter)]' },
  };
  const { label, cls } = map[state] ?? { label: state, cls: 'bg-[var(--hover-tint)] text-[var(--text-ter)]' };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

function TournamentStateBadge({ state }: { state: Tournament['state'] }) {
  const map: Record<Tournament['state'], { label: string; cls: string }> = {
    DRAFT:            { label: 'Draft',        cls: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' },
    READY:            { label: 'Ready',        cls: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
    IN_PROGRESS:      { label: 'In Progress',  cls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
    AWAITING_ADVANCE: { label: 'Needs Review', cls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
    COMPLETE:         { label: 'Complete',     cls: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
    CANCELLED:        { label: 'Cancelled',    cls: 'bg-[var(--coral-dim)] text-[var(--coral)]' },
  };
  const { label, cls } = map[state] ?? { label: state, cls: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

function RoundStateIcon({ state }: { state: TournamentRound['state'] }) {
  if (state === 'COMPLETE')  return <CheckCircle className="h-4 w-4 text-[var(--green)]" />;
  if (state === 'OPEN')      return <Clock className="h-4 w-4 text-[var(--mustard)]" />;
  if (state === 'ADVANCING') return <AlertCircle className="h-4 w-4 text-[var(--mustard)]" />;
  if (state === 'SKIPPED')   return <X className="h-4 w-4 text-[var(--text-ter)]" />;
  return <Circle className="h-4 w-4 text-[var(--text-ter)]" />;
}

// ─── Item lookup helper ───────────────────────────────────────────────────────

function useItemTitle(id: string): string {
  const item = useItemsStore(s => s.getItemById(id));
  return item?.title ?? id;
}

function ItemChip({ id, rank, advancing }: { id: string; rank?: number; advancing?: boolean }) {
  const item = useItemsStore(s => s.getItemById(id));
  const title = item?.title ?? id;
  return (
    <div className={`flex items-center gap-2 px-3 py-2  border text-sm ${
      advancing === true
        ? 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]'
        : advancing === false
          ? 'bg-[var(--surface)] border-[var(--rule)] text-[var(--text-ter)] line-through'
          : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-sec)]'
    }`}>
      {rank != null && (
        <span className={`text-xs font-bold w-5 text-center shrink-0 ${advancing ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
          {rank}
        </span>
      )}
      <span className="truncate">{title}</span>
      {advancing === true && <Check className="h-3.5 w-3.5 text-[var(--green)] shrink-0" />}
    </div>
  );
}

// ─── Advancement review panel ─────────────────────────────────────────────────

function AdvancementReviewPanel({
  tournament, round,
  onConfirm,
}: {
  tournament: Tournament;
  round: TournamentRound;
  onConfirm: (advancingIds: string[]) => void;
}) {
  const sessions = useSessionStore(s => s.sessions);
  const { pendingAdvancement } = useTournamentStore();

  // Get ranked output from sessions for this round
  const rankedFromSessions = useMemo(() => {
    const roundSessions = round.sessionIds
      .map(id => sessions.find(s => s.id === id))
      .filter(Boolean);

    if (roundSessions.length === 0) return null;

    // Build group results for advancement calc
    const groupResults: Record<string, { rankedItemIds: string[]; scores: Record<string, number> }> = {};
    roundSessions.forEach(sess => {
      if (sess?.rankingOutput) {
        groupResults[sess.id] = {
          rankedItemIds: sess.rankingOutput.rankedItemIds,
          scores: sess.rankingOutput.normalizedScores,
        };
      }
    });

    if (Object.keys(groupResults).length === 0) return null;

    return groupResults;
  }, [round.sessionIds, sessions]);

  const defaultResult = useMemo<AdvancementResult | null>(() => {
    if (pendingAdvancement?.roundId === round.id) {
      return pendingAdvancement.calculated;
    }
    if (!rankedFromSessions) return null;
    // Use per-round k override when available
    const advancementRule = round.advancingK != null
      ? { ...tournament.config.advancementRule, type: 'top_k' as const, k: round.advancingK }
      : tournament.config.advancementRule;
    return calculateAdvancementResult({
      round,
      advancementRule,
      groupResults: rankedFromSessions,
    });
  }, [pendingAdvancement, round, tournament.config.advancementRule, rankedFromSessions]);

  // Editable cut-line position
  const [advancingIds, setAdvancingIds] = useState<string[]>(() =>
    defaultResult?.advancingItemIds ?? []
  );
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (defaultResult) {
      setAdvancingIds(defaultResult.advancingItemIds);
    }
  }, [defaultResult]);

  // Get all ranked items from the sessions
  const allRanked = useMemo(() => {
    if (!rankedFromSessions) {
      // Fall back to round's item pool items
      return tournament.itemIds ?? [];
    }
    // Merge rankings from all sessions
    const merged = new Map<string, number>();
    Object.values(rankedFromSessions).forEach(({ rankedItemIds, scores }) => {
      rankedItemIds.forEach((id, idx) => {
        const score = scores[id] ?? (rankedItemIds.length - idx);
        merged.set(id, (merged.get(id) ?? 0) + score);
      });
    });
    return [...merged.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => id);
  }, [rankedFromSessions, tournament.itemIds]);

  // Items to show
  const displayItems = showAll ? allRanked : allRanked.slice(0, Math.max(advancingIds.length + 3, 5));

  const toggleAdvancing = (id: string) => {
    setAdvancingIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  if (allRanked.length === 0) {
    return (
      <div className="p-4 text-sm text-[var(--text-ter)] text-center">
        No session results available yet. Run the session first.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-[var(--text-sec)] mb-1">
          Items below are ranked by session output. Toggle which ones advance to the next round.
        </p>
        <p className="text-xs text-[var(--text-ter)]">
          {advancingIds.length} item{advancingIds.length !== 1 ? 's' : ''} will advance
        </p>
      </div>

      <div className="space-y-1.5">
        {displayItems.map((id, idx) => {
          const isAdvancing = advancingIds.includes(id);
          return (
            <button
              key={id}
              onClick={() => toggleAdvancing(id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5  border text-left transition-colors ${
                isAdvancing
                  ? 'bg-[var(--green-dim)] border-[var(--green)]'
                  : 'bg-[var(--bg-raised)] border-[var(--rule)] hover:border-[var(--rule)]'
              }`}
            >
              <span className="w-6 text-xs text-[var(--text-ter)] text-right shrink-0">{idx + 1}</span>
              <ItemTitle id={id} />
              <div className="ml-auto shrink-0">
                {isAdvancing
                  ? <Check className="h-4 w-4 text-[var(--green)]" />
                  : <X className="h-4 w-4 text-[var(--text-ter)]" />}
              </div>
            </button>
          );
        })}

        {allRanked.length > displayItems.length && (
          <button
            onClick={() => setShowAll(true)}
            className="w-full text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] py-2 text-center"
          >
            Show {allRanked.length - displayItems.length} more items
          </button>
        )}
      </div>

      <button
        onClick={() => onConfirm(advancingIds)}
        disabled={advancingIds.length === 0}
        className={`w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold  transition-colors ${
          advancingIds.length > 0
            ? 'bg-[var(--green)] hover:bg-[var(--green)] text-white'
            : 'bg-[var(--hover-tint)] text-[var(--text-ter)] cursor-not-allowed'
        }`}
      >
        Confirm advancement — {advancingIds.length} item{advancingIds.length !== 1 ? 's' : ''}
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function ItemTitle({ id }: { id: string }) {
  const title = useItemTitle(id);
  return <span className="text-sm text-[var(--text-pri)] truncate flex-1">{title}</span>;
}

// ─── Round participants panel ─────────────────────────────────────────────────

function RoundParticipantsPanel({
  tournament,
  round,
}: {
  tournament: Tournament;
  round: TournamentRound;
}) {
  const updateRoundParticipants      = useTournamentStore(s => s.updateRoundParticipants);
  const updateTournamentParticipants = useTournamentStore(s => s.updateTournamentParticipants);
  const { users } = useSystemUsers();
  const [editing, setEditing]        = useState(false);
  const [saving, setSaving]          = useState(false);

  const mode            = tournament.config.participantMode;
  const defaultIds      = tournament.config.defaultParticipantIds ?? [];
  const roundIds        = round.participantIds ?? [];
  const isReadonly      = round.state === 'COMPLETE' || round.state === 'SKIPPED';
  const isEditable      = !isReadonly && (mode === 'per_round' || mode === 'fixed');

  // Local draft state while editing
  const [draftRoundIds, setDraftRoundIds]   = useState<string[]>(roundIds);
  const [draftDefaultIds, setDraftDefaultIds] = useState<string[]>(defaultIds);

  const effectiveIds = mode === 'fixed'
    ? defaultIds
    : mode === 'per_round'
      ? (roundIds.length > 0 ? roundIds : defaultIds)
      : [];

  const resolveNames = (ids: string[]) =>
    ids.map(id => {
      const u = users.find(x => x.id === id);
      return u?.name || u?.email || id;
    });

  const handleSave = async () => {
    setSaving(true);
    try {
      if (mode === 'per_round') {
        await updateRoundParticipants(round.id, draftRoundIds);
      } else if (mode === 'fixed') {
        await updateTournamentParticipants(tournament.id, 'fixed', draftDefaultIds);
      }
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraftRoundIds(roundIds);
    setDraftDefaultIds(defaultIds);
    setEditing(false);
  };

  const participantCount = effectiveIds.length;

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
      <div className="px-3 py-2.5 flex items-center justify-between border-b border-[var(--rule)]">
        <div className="flex items-center gap-2">
          <Users className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          <span className="text-xs font-semibold text-[var(--text-sec)]">
            Participants
          </span>
          {mode === 'open' && (
            <span className="text-xs text-[var(--text-ter)]">· Open</span>
          )}
        </div>
        {isEditable && !editing && (
          <button
            type="button"
            onClick={() => {
              setDraftRoundIds(roundIds);
              setDraftDefaultIds(defaultIds);
              setEditing(true);
            }}
            className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors"
          >
            <Pencil className="h-3 w-3" /> Edit
          </button>
        )}
      </div>

      <div className="p-3">
        {mode === 'open' && (
          <p className="text-xs text-[var(--text-ter)]">Any workspace member may join sessions in this tournament.</p>
        )}

        {(mode === 'fixed' || mode === 'per_round') && !editing && (
          <>
            {participantCount === 0 ? (
              <p className="text-xs text-[var(--text-ter)] italic">
                {mode === 'per_round' && round.roundNumber > 1
                  ? 'Inheriting from tournament default (none set)'
                  : 'No participants assigned'}
                {isEditable && (
                  <button
                    type="button"
                    onClick={() => { setDraftRoundIds(roundIds); setDraftDefaultIds(defaultIds); setEditing(true); }}
                    className="ml-1.5 text-[var(--text-ter)] hover:text-[var(--text-ter)] not-italic"
                  >
                    Add participants
                  </button>
                )}
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {resolveNames(effectiveIds).map((name, i) => (
                  <span
                    key={effectiveIds[i]}
                    className="inline-flex items-center text-xs bg-[var(--hover-tint)] text-[var(--text-sec)] rounded-full px-2.5 py-1"
                  >
                    <span className="w-4 h-4 rounded-full bg-[var(--text-ter)] text-[var(--text-sec)] flex items-center justify-center text-[9px] font-semibold mr-1.5 shrink-0">
                      {name[0]?.toUpperCase() ?? '?'}
                    </span>
                    {name}
                  </span>
                ))}
                {mode === 'per_round' && roundIds.length === 0 && defaultIds.length > 0 && (
                  <span className="text-xs text-[var(--text-ter)] self-center">inherited from default</span>
                )}
              </div>
            )}
          </>
        )}

        {editing && (
          <div className="space-y-3">
            {mode === 'per_round' && (
              <TournamentParticipantEditor
                mode="per_round"
                defaultParticipantIds={draftDefaultIds}
                roundConfigs={[{
                  roundNumber: round.roundNumber,
                  label: round.label ?? `Round ${round.roundNumber}`,
                  participantIds: draftRoundIds,
                }]}
                onModeChange={() => {}}
                onDefaultParticipantsChange={() => {}}
                onRoundParticipantsChange={(_, ids) => setDraftRoundIds(ids)}
                readonly={false}
              />
            )}
            {mode === 'fixed' && (
              <TournamentParticipantEditor
                mode="fixed"
                defaultParticipantIds={draftDefaultIds}
                roundConfigs={[]}
                onModeChange={() => {}}
                onDefaultParticipantsChange={setDraftDefaultIds}
                onRoundParticipantsChange={() => {}}
                readonly={false}
              />
            )}
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Round detail panel ───────────────────────────────────────────────────────

function RoundDetailPanel({
  tournament, round, roundItems,
  onOpenRound, onMarkSessionsComplete, onConfirmAdvancement,
}: {
  tournament: Tournament;
  round: TournamentRound;
  roundItems: string[];
  onOpenRound: () => Promise<void>;
  onMarkSessionsComplete: () => void;
  onConfirmAdvancement: (advancingIds: string[]) => void;
}) {
  const sessions = useSessionStore(s => s.sessions);
  const roundSessions = round.sessionIds
    .map(id => sessions.find(s => s.id === id))
    .filter(Boolean);

  const toolLabel = TOOL_LABELS[round.toolId ?? tournament.config.defaultToolId ?? ''] ?? round.toolId;

  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);

  const handleOpen = async () => {
    setOpening(true);
    setOpenError(null);
    try {
      await onOpenRound();
    } catch (e) {
      setOpenError(e instanceof Error ? e.message : 'Failed to open round');
    } finally {
      setOpening(false);
    }
  };

  const allPrereqsMet = round.prerequisites.every(p => p.met);

  return (
    <div className="space-y-5">
      {/* Round header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-[var(--text-pri)]">{round.label ?? `Round ${round.roundNumber}`}</h3>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            {roundItems.length} item{roundItems.length !== 1 ? 's' : ''} competing
            {toolLabel && <> · {toolLabel}</>}
          </p>
        </div>
        <StateBadge state={round.state} />
      </div>

      {/* Participants */}
      <RoundParticipantsPanel tournament={tournament} round={round} />

      {/* Prerequisites */}
      {round.prerequisites.length > 0 && round.state === 'PENDING' && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-[var(--text-ter)]">Prerequisites</p>
          {round.prerequisites.map((p, i) => (
            <div key={i} className={`flex items-center gap-2 text-xs ${p.met ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
              {p.met ? <CheckCircle className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5 text-[var(--text-ter)]" />}
              {p.type.replace(/_/g, ' ').toLowerCase()}
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {openError && (
        <div className="flex items-center gap-2 text-xs text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] px-3 py-2">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {openError}
        </div>
      )}

      {/* Action: PENDING → Open Round */}
      {round.state === 'PENDING' && (
        <button
          onClick={handleOpen}
          disabled={opening || !allPrereqsMet}
          className={`w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium  transition-colors ${
            allPrereqsMet && !opening
              ? 'bg-[var(--slate)] hover:bg-[var(--slate)] text-white'
              : 'bg-[var(--hover-tint)] text-[var(--text-ter)] cursor-not-allowed'
          }`}
        >
          {opening ? <><Loader2 className="h-4 w-4 animate-spin" /> Opening…</> : <><Play className="h-4 w-4" /> Open Round</>}
        </button>
      )}

      {/* Sessions list */}
      {roundSessions.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-[var(--text-ter)]">Session{roundSessions.length > 1 ? 's' : ''}</p>
          {roundSessions.map(sess => sess && (
            <div key={sess.id} className="flex items-center justify-between bg-[var(--surface)] border border-[var(--rule)] px-3 py-2.5">
              <div>
                <p className="text-sm text-[var(--text-sec)] font-medium">{sess.name}</p>
                <p className="text-xs text-[var(--text-ter)] mt-0.5 capitalize">{sess.state.toLowerCase().replace('_', ' ')}</p>
              </div>
              <Link
                to={`/prioritization/sessions/${sess.id}`}
                className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium"
              >
                Open <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Action: OPEN → Mark sessions complete (manual for MVP) */}
      {round.state === 'OPEN' && (
        <div className="space-y-2">
          <p className="text-xs text-[var(--text-ter)]">
            After all participants complete their sessions, mark them complete to proceed.
          </p>
          <button
            onClick={onMarkSessionsComplete}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium bg-[var(--mustard-dim)] border border-[var(--mustard)] text-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition-colors"
          >
            <CheckCircle className="h-4 w-4" />
            Mark sessions complete
          </button>
        </div>
      )}

      {/* Action: SESSIONS_COMPLETE → Advancement review */}
      {(round.state === 'SESSIONS_COMPLETE' || round.state === 'ADVANCING') && (
        <AdvancementReviewPanel
          tournament={tournament}
          round={round}
          onConfirm={onConfirmAdvancement}
        />
      )}

      {/* COMPLETE — show advancing items */}
      {round.state === 'COMPLETE' && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-[var(--text-ter)]">
            {round.advancingItemIds?.length ?? 0} items advanced
          </p>
          <div className="space-y-1.5">
            {(round.advancingItemIds ?? []).map((id, i) => (
              <ItemChip key={id} id={id} rank={i + 1} advancing={true} />
            ))}
          </div>
          {(round.eliminatedItemIds ?? []).length > 0 && (
            <EliminatedItems ids={round.eliminatedItemIds ?? []} />
          )}
        </div>
      )}

      {/* Items in this round */}
      {round.state !== 'COMPLETE' && roundItems.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-[var(--text-ter)]">Items in this round ({roundItems.length})</p>
          {roundItems.map(id => (
            <ItemChip key={id} id={id} />
          ))}
        </div>
      )}
    </div>
  );
}

function EliminatedItems({ ids }: { ids: string[] }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <button
        onClick={() => setExpanded(p => !p)}
        className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
      >
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {ids.length} eliminated
      </button>
      {expanded && (
        <div className="mt-1.5 space-y-1">
          {ids.map(id => <ItemChip key={id} id={id} advancing={false} />)}
        </div>
      )}
    </div>
  );
}

// ─── Final results panel ──────────────────────────────────────────────────────

function FinalResultsPanel({ tournament }: { tournament: Tournament }) {
  const finalIds = tournament.finalRankedItemIds ?? [];
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Award className="h-5 w-5 text-[var(--mustard)]" />
        <h3 className="text-base font-semibold text-[var(--text-pri)]">Final Results</h3>
      </div>
      <div className="space-y-1.5">
        {finalIds.map((id, i) => (
          <ItemChip key={id} id={id} rank={i + 1} advancing={true} />
        ))}
        {finalIds.length === 0 && (
          <p className="text-sm text-[var(--text-ter)]">No final results yet.</p>
        )}
      </div>
      {finalIds.length > 0 && <ExportFinalResult tournament={tournament} />}
    </div>
  );
}

/**
 * S-14: the final result becomes a proposed priority list on the tournament's
 * track, which is then decided, ratified and published like any other list.
 */
function ExportFinalResult({ tournament }: { tournament: Tournament }) {
  const exportFinalResult = useTournamentStore(s => s.exportFinalResult);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listId = tournament.publishedPrioritySetId;

  if (listId) {
    return (
      <div className="pt-2 border-t border-[var(--rule)]">
        <Link
          to={`/prioritization/reports/priority-set/${listId}`}
          className="flex items-center gap-2 text-sm font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)]"
        >
          <Award className="h-4 w-4" />
          Open the priority list made from this result
        </Link>
      </div>
    );
  }
  if (!tournament.cycleId || !tournament.trackId) {
    return (
      <p className="pt-2 border-t border-[var(--rule)] text-xs text-[var(--text-ter)]">
        A priority list belongs to a cycle’s track. This tournament has none, so its result stays here.
      </p>
    );
  }
  return (
    <div className="pt-2 border-t border-[var(--rule)]">
      <p className="text-xs text-[var(--text-ter)] mb-2">
        Make this result a proposed priority list on the track. It is then decided, ratified and published there.
      </p>
      <button
        type="button"
        disabled={busy}
        className="flex items-center gap-2 text-sm font-medium text-[var(--text-sec)] hover:text-[var(--text-pri)] disabled:opacity-50"
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            await exportFinalResult(tournament.id);
          } catch (err) {
            setError(err instanceof Error ? err.message : 'The priority list could not be made.');
          } finally {
            setBusy(false);
          }
        }}
      >
        <Award className="h-4 w-4" />
        {busy ? 'Making the list…' : 'Make it a priority list'}
      </button>
      {error && <p role="alert" className="mt-2 text-xs text-[var(--coral)]">{error}</p>}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function TournamentDetail() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const userId    = useUserStore(s => s.userId);

  const tournament      = useTournamentStore(s => tournamentId ? s.tournaments[tournamentId] : undefined);
  // Select the two Records with stable references, then derive the rounds array
  // in useMemo — avoids creating a new array inside the selector on every render.
  const roundsMap       = useTournamentStore(s => s.rounds);
  const roundStubs      = tournament?.rounds;
  const rounds          = useMemo<TournamentRound[]>(() => {
    if (!roundStubs) return [];
    return roundStubs.map(r => roundsMap[r.id]).filter(Boolean) as TournamentRound[];
  }, [roundStubs, roundsMap]);

  const openRound          = useTournamentStore(s => s.openRound);
  const markSessionsComplete = useTournamentStore(s => s.markSessionsComplete);
  const confirmAdvancement = useTournamentStore(s => s.confirmAdvancement);
  const readyTournament    = useTournamentStore(s => s.readyTournament);
  const loadTournament     = useTournamentStore(s => s.loadTournament);
  const { showToast } = useToast();

  const [selectedRoundId, setSelectedRoundId] = useState<string | null>(null);
  const [showFinal, setShowFinal]             = useState(false);

  // Load / reconstruct from DB on mount (Build Contract §2.4 — refresh-safe)
  useEffect(() => {
    if (tournamentId) loadTournament(tournamentId);
  }, [tournamentId, loadTournament]);

  // Auto-select first incomplete round
  useEffect(() => {
    if (selectedRoundId) return;
    const active = rounds.find(r => r.state !== 'COMPLETE' && r.state !== 'SKIPPED');
    if (active) setSelectedRoundId(active.id);
    else if (rounds[0]) setSelectedRoundId(rounds[0].id);
  }, [rounds, selectedRoundId]);

  const selectedRound = rounds.find(r => r.id === selectedRoundId) ?? null;

  // Get items for a round: round 1 uses tournament.itemIds; subsequent rounds use prior round's advancingItemIds
  const getRoundItems = useCallback((round: TournamentRound): string[] => {
    if (!tournament) return [];
    if (round.roundNumber === 1) return tournament.itemIds ?? [];
    const priorRound = rounds.find(r => r.roundNumber === round.roundNumber - 1);
    return priorRound?.advancingItemIds ?? [];
  }, [tournament, rounds]);

  // Open a round: create a session + update store
  const handleOpenRound = useCallback(async (round: TournamentRound) => {
    if (!tournament || !userId) return;
    const itemIds = getRoundItems(round);
    const toolId  = round.toolId ?? tournament.config.defaultToolId ?? 'stack_ranking';

    // Create the session
    const result = await createSessionWithFeedback({
      name: `${tournament.name} — ${round.label ?? `Round ${round.roundNumber}`}`,
      facilitatorId: userId,
      createdBy: userId,
      intent: 'rank_priorities',
      initialItemIds: itemIds,
    }, { toast: showToast });

    if (result.ok) {
      const newSessionIds = [result.sessionId!];
      // Patch session ID onto the round in the store + persist to DB
      useTournamentStore.setState(s => ({
        rounds: {
          ...s.rounds,
          [round.id]: {
            ...s.rounds[round.id],
            sessionIds: newSessionIds,
          },
        },
      }));
      await persistRoundSessionIds(round.id, newSessionIds);

      // Also set the selected tool on the session
      if (toolId) {
        await useSessionStore.getState().setSelectedTool(result.sessionId!, toolId);
      }
    } else {
      showToast('error', 'Could not open this round', result.error);
      return;
    }

    // Transition round to OPEN
    await openRound(tournament.id, round.id);
  }, [tournament, userId, getRoundItems, openRound, showToast]);

  // Mark sessions complete (manual gate in MVP) — written to the database by the store
  const handleMarkSessionsComplete = useCallback(async (round: TournamentRound) => {
    if (!tournament) return;
    try {
      await markSessionsComplete(tournament.id, round.id);
    } catch (e) {
      showToast('error', 'Could not mark the sessions complete', e instanceof Error ? e.message : undefined);
    }
  }, [tournament, markSessionsComplete, showToast]);

  // Confirm advancement — pass allItemIds so eliminated set is computed correctly
  const handleConfirmAdvancement = useCallback(async (round: TournamentRound, advancingIds: string[]) => {
    if (!tournament) return;
    const isLastRound = round.roundNumber === rounds.length;
    await confirmAdvancement({
      tournamentId: tournament.id,
      roundId: round.id,
      advancingItemIds: advancingIds,
      allItemIds: getRoundItems(round),
    });
    if (isLastRound) setShowFinal(true);
  }, [tournament, rounds, confirmAdvancement, getRoundItems]);

  // Ready tournament (DRAFT → READY)
  const handleReady = useCallback(async () => {
    if (!tournament) return;
    try {
      await readyTournament(tournament.id);
    } catch (e) {
      // silently handle — guard may fail if config incomplete
      console.error(e);
    }
  }, [tournament, readyTournament]);

  if (!tournament) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[var(--text-ter)] text-sm">Tournament not found.</p>
      </div>
    );
  }

  const isComplete = tournament.state === 'COMPLETE';

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] max-md:h-auto">
      {/* Page header */}
      <div className="px-4 md:px-6 py-4 border-b border-[var(--rule)] bg-[var(--bg-raised)] flex flex-wrap items-center justify-between gap-3 flex-shrink-0">
        <div>
          <Link
            to="/prioritization/tournaments"
            className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] flex items-center gap-1 mb-1"
          >
            <ArrowLeft className="h-3 w-3" /> Tournaments
          </Link>
          <h1 className="text-xl font-bold text-[var(--text-pri)] flex items-center gap-2">
            <Trophy className="h-5 w-5 text-[var(--text-sec)]" />
            {tournament.name}
          </h1>
          {tournament.description && (
            <p className="text-sm text-[var(--text-ter)] mt-0.5">{tournament.description}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <TournamentStateBadge state={tournament.state} />
          {tournament.state === 'DRAFT' && rounds.length > 0 && (
            <button
              onClick={handleReady}
              className="text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] px-3 py-1.5 border border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors"
            >
              Mark Ready
            </button>
          )}
        </div>
      </div>

      {/* Split pane — side by side on a computer, the bracket above the round on a phone */}
      <div className="flex flex-1 overflow-hidden max-md:flex-col max-md:overflow-visible">

        {/* ── Left: bracket rail ── */}
        <div className="w-64 shrink-0 border-r border-[var(--rule)] bg-[var(--bg-raised)] overflow-y-auto flex flex-col max-md:w-full max-md:border-r-0 max-md:border-b max-md:overflow-visible">
          <div className="px-3 py-3 border-b border-[var(--rule)]">
            <p className="text-xs text-[var(--text-ter)] font-medium uppercase tracking-wide">Bracket</p>
          </div>
          <div className="flex-1 py-2">
            {rounds.map((round) => {
              const itemCount = getRoundItems(round).length;
              const isActive  = selectedRoundId === round.id;
              return (
                <button
                  key={round.id}
                  onClick={() => { setSelectedRoundId(round.id); setShowFinal(false); }}
                  className={`w-full text-left px-3 py-3 transition-colors border-l-2 ${
                    isActive
                      ? 'bg-[var(--slate-dim)] border-l-blue-500'
                      : 'border-l-transparent hover:bg-[var(--surface)]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <RoundStateIcon state={round.state} />
                      <span className="text-sm text-[var(--text-sec)] font-medium truncate">
                        {round.label ?? `Round ${round.roundNumber}`}
                      </span>
                    </div>
                    <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)] shrink-0" />
                  </div>
                  <p className="text-xs text-[var(--text-ter)] mt-0.5 pl-6">
                    {itemCount} item{itemCount !== 1 ? 's' : ''}
                    {round.state === 'COMPLETE' && round.advancingItemIds != null
                      ? ` → ${round.advancingItemIds.length} advance`
                      : ''}
                  </p>
                </button>
              );
            })}

            {/* Final results link */}
            {isComplete && (
              <button
                onClick={() => { setShowFinal(true); setSelectedRoundId(null); }}
                className={`w-full text-left px-3 py-3 transition-colors border-l-2 ${
                  showFinal
                    ? 'bg-[var(--mustard-dim)] border-l-amber-500'
                    : 'border-l-transparent hover:bg-[var(--surface)]'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-[var(--mustard)]" />
                  <span className="text-sm text-[var(--mustard)] font-semibold">Final Results</span>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* ── Right: detail pane ── */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-6 max-md:overflow-visible min-w-0">
          {showFinal ? (
            <FinalResultsPanel tournament={tournament} />
          ) : selectedRound ? (
            <RoundDetailPanel
              tournament={tournament}
              round={selectedRound}
              roundItems={getRoundItems(selectedRound)}
              onOpenRound={() => handleOpenRound(selectedRound)}
              onMarkSessionsComplete={() => handleMarkSessionsComplete(selectedRound)}
              onConfirmAdvancement={(ids) => handleConfirmAdvancement(selectedRound, ids)}
            />
          ) : rounds.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-20">
              <Trophy className="h-10 w-10 text-[var(--text-ter)] mb-3" />
              <p className="text-sm text-[var(--text-ter)]">No rounds configured.</p>
              <p className="text-xs text-[var(--text-ter)] mt-1">
                Edit the tournament to add rounds.
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-20">
              <Circle className="h-10 w-10 text-[var(--text-ter)] mb-3" />
              <p className="text-sm text-[var(--text-ter)]">Select a round from the bracket.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TournamentDetail;
